import React, { useEffect, useState } from "react";
import { useUser } from "../../Context/UserContext";
import { default as Header } from "../../Components/Header2/Header2";
import { default as Footer } from "../../Components/Footer/FooterSmall";
import useAPI from "../../Hooks/useAPI";
import ProfileForm from "./ProfileForm";
import SecHeader from "../../Components/SecHeader/SecHeader";
import "../../Components/Page/Page.css";
import "./Profile.css";
import ContractingRedirect, {
  UserData,
} from "../../Components/Contracting/Contracting";
import Button2 from "../../Components/Button2/Button2";

export interface UserProfileData extends UserData {
  mndaStatus: string;
}

const Profile: React.FC = () => {
  const { user, setUser, hasPermission } = useUser();
  const { updateUser, postUserData, saveContact } = useAPI();
  const [isEditing, setIsEditing] = useState(false);
  const [countrycode, setCountryCode] = useState("");
  const [, setPhoneNoCode] = useState("");

  const [userData, setFormData] = useState<UserProfileData>({
    source: "",
    company: "",
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    mndaStatus: "",
    title: "",
    id: "",
    entityId: "",
    streetaddress: "",
    streetaddress2: "",
    city: "",
    state: "",
    zip: "",
    contractAdmin: "",
  });

  useEffect(() => {
    // 1. Always initialize with whatever user context is currently available
    if (user) {
      setFormData({
        source: "Contracts<32Gem$",
        company: user.companyName || "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        phone: user.phone || "",
        email: user.email || "",
        mndaStatus: user.mndaStatus || "",
        title: user.title || "",
        id: user.id || "",
        entityId: user.entityid || "",
        streetaddress: user.streetaddress || "",
        streetaddress2: user.streetaddress2 || "",
        city: user.city || "",
        state: user.state || "",
        zip: user.zip || "",
        contractAdmin: String(hasPermission("admin_contracts")) || "",
      });
    }
  }, [user?.id]); // runs when user is ready

  //console.log("localUser", user);

  useEffect(() => {
    const fetchAndUpdateUser = async () => {
      if (!user?.id) return;

      try {
        const freshUser = await postUserData(user.email);

        // merge with any local or default data
        const updatedUserData: UserProfileData = {
          source: "Contracts<32Gem$",
          company: freshUser.user.companyName ?? user.companyName ?? "",
          firstName: freshUser.user.firstName ?? user.firstName ?? "",
          lastName: freshUser.user.lastName ?? user.lastName ?? "",
          phone: freshUser.user.phone ?? user.phone ?? "",
          email: freshUser.user.email ?? user.email ?? "",
          mndaStatus: freshUser.user.mndaStatus ?? user.mndaStatus ?? "",
          title: freshUser.user.title ?? user.title ?? "",
          id: freshUser.user.id ?? user.id ?? "",
          entityId: freshUser.user.entityid ?? user.entityid ?? "",
          streetaddress:
            freshUser.user.streetaddress ?? user.streetaddress ?? "",
          streetaddress2:
            freshUser.user.streetaddress2 ?? user.streetaddress2 ?? "",
          city: freshUser.user.city ?? user.city ?? "",
          state: freshUser.user.state ?? user.state ?? "",
          zip: freshUser.user.zip ?? user.zip ?? "",
          contractAdmin:
            freshUser.user.contractadmin ??
            String(hasPermission("admin_contracts")) ??
            "",
        };

        // Update local user context
        if (!isEditing) {
          setUser({
            ...user,
            ...updatedUserData,
          });
        }
        setFormData(updatedUserData);
      } catch (err) {
        console.error("Error refreshing user data:", err);
        // fallback: use existing user context
        const localUserData: UserProfileData = {
          source: "Contracts<32Gem$",
          company: String(user.companyName || ""),
          firstName: String(user.firstName || ""),
          lastName: String(user.lastName || ""),
          phone: String(user.phone || ""),
          email: String(user.email || ""),
          mndaStatus: String(user.mndaStatus || ""),
          title: String(user.title || ""),
          id: String(user.id || ""),
          entityId: String(user.entityid || ""),
          streetaddress: String(user.streetaddress || ""),
          streetaddress2: String(user.streetaddress2 || ""),
          city: String(user.city || ""),
          state: String(user.state || ""),
          zip: String(user.zip || ""),
          contractAdmin: String(hasPermission("admin_contracts")),
        };
        setFormData(localUserData);
      }
    };

    fetchAndUpdateUser();
  }, [user!.id]);

  const formatPhoneNumber = (phoneNumber: string): string => {
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, "");

    // If the number is 11 digits and starts with 1 (US number)
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
      const usNumber = cleaned.slice(1);
      return `(${usNumber.slice(0, 3)}) ${usNumber.slice(
        3,
        6,
      )}-${usNumber.slice(6)}`;
    }

    // If the number is 10 digits (US number without country code)
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6,
      )}`;
    }

    // For international numbers, format as +XX XXX XXX XXXX
    // This handles numbers with country codes of varying lengths
    const countryCode = cleaned.slice(0, cleaned.length - 10);
    const nationalNumber = cleaned.slice(cleaned.length - 10);

    return `+${countryCode} ${nationalNumber.slice(
      0,
      3,
    )} ${nationalNumber.slice(3, 6)} ${nationalNumber.slice(6)}`;
  };

  const getStatusStyle = (status: string) => {
    const lower = status.toLowerCase();
    if (lower.includes("rejected")) {
      return { background: "red", color: "white" };
    } else if (lower.includes("approved")) {
      return { background: "green", color: "white" };
    } else if (lower.includes("pending")) {
      return { background: "orange", color: "black" };
    } else if (lower.includes("started")) {
      return { background: "yellow", color: "black" };
    } else {
      return {};
    }
  };

  const handleSubmit = async (formData: any) => {
    if (!user?.id) {
      throw new Error("User ID not found");
    }

    await updateUser(
      user.id,
      {
        ...formData,
        email: user.email,
      },
      user.id,
    );
    // use the latest input value, not user.phone
    const phoneInput = formData.phone?.trim() || "";

    let derivedCountryCode = countrycode;
    let derivedPhoneNoCode = phoneInput;

    if (phoneInput) {
      const cleaned = phoneInput.replace(/\D/g, "");

      if (cleaned.length === 11 && cleaned.startsWith("1")) {
        derivedCountryCode = cleaned[0]; // '1'
        derivedPhoneNoCode = cleaned.slice(1); // remove country code
      } else {
        derivedPhoneNoCode = cleaned; // just digits
      }
    }

    // update UI state if needed
    setCountryCode(derivedCountryCode);
    setPhoneNoCode(derivedPhoneNoCode);

    const response = await saveContact(
      {
        ...formData,
        email: user.email,
        countrycode: derivedCountryCode,
        phonenocode: derivedPhoneNoCode,
        streetaddress1: user.streetaddress,
        agreeToTerms: true,
        datasource: "",
        ipaddress: "",
      },
      false,
      user.cidn,
    );

    // Update local user context
    setUser({
      ...user,
      ...formData,
    });

    setIsEditing(false);
  };

  if (!user) {
    return <div>No user data available.</div>;
  }

  return (
    <div className="generalPage">
      <Header />
      <div className="pageContainer">
        <div className="profile-title-container">
          <div className="sectionContainer">
            <SecHeader
              title={isEditing ? "Edit Profile" : "Profile"}
              iSize={3}
            />
          </div>
          <div className="profile-container">
            {isEditing ? (
              <ProfileForm
                user={user}
                onSubmit={handleSubmit}
                onCancel={() => setIsEditing(false)}
              />
            ) : (
              <div className="profile-card two-profile-column">
                {/* LEFT PANEL */}
                <div className="left profile-panel">
                  <div className="user-header">
                    <div className="avatar">
                      {user.firstName?.[0]}
                      {user.lastName?.[0]}
                      <span id="profile-middleName" style={{ display: "none" }}>
                        {user.middleName}
                      </span>
                    </div>
                    <div>
                      <div className="user-name">
                        <span id="profile-firstName">{user.firstName}</span>{" "}
                        {user.middleName && `${user.middleName?.[0]} `}
                        <span id="profile-lastName">{user.lastName}</span>
                      </div>
                      <div className="user-email" id="email">
                        {user.email}
                      </div>
                    </div>
                  </div>
                  <div className="profile-edit-section">
                    <Button2
                      name="submit"
                      text="Edit Profile"
                      type="button"
                      classname="submit-btn"
                      onClick={() => setIsEditing(!isEditing)}
                    />
                  </div>
                  <div className="section ">
                    <div className="contracting-redirect ">
                      <ContractingRedirect {...userData} />
                    </div>
                  </div>
                  <div className="section">
                    {!user?.mndaStatus ||
                    Object.keys(user.mndaStatus).length === 0 ? (
                      <div className="badge muted">None</div>
                    ) : (
                      Object.entries(user.mndaStatus).map(
                        ([type, status], index) => (
                          <div
                            key={index}
                            className="badge"
                            style={getStatusStyle(status)}
                          >
                            {status}
                          </div>
                        ),
                      )
                    )}
                  </div>
                </div>

                {/* RIGHT PANEL */}
                <div className="right profile-panel">
                  <div className="grid-2">
                    <div className="card-section">
                      <div className="section-title">Personal Info</div>

                      <div className="info">
                        {[user.streetaddress, user.streetaddress2]
                          .filter(Boolean)
                          .map((line, i) => (
                            <div key={i}>{line}</div>
                          ))}

                        <div>
                          {[user.city, user.state, user.zip]
                            .filter(Boolean)
                            .join(", ")}
                        </div>

                        {user.country && <div id="country">{user.country}</div>}
                        {user.phone && (
                          <div id="phone">{formatPhoneNumber(user.phone)}</div>
                        )}
                      </div>
                    </div>

                    <div className="card-section">
                      <div className="section-title">Company Info</div>

                      {user.companyName && (
                        <div id="company">{user.companyName}</div>
                      )}
                      {user.companyType && (
                        <div id="companyType">{user.companyType}</div>
                      )}
                      {user.title && <div id="title">{user.title}</div>}
                    </div>

                    <div className="card-section">
                      <div className="section-title">Professions</div>
                      <div id="profession">
                        {user.profession?.join(", ")}
                        {user.professionOther &&
                          user.profession?.includes("Other") && (
                            <span> ({user.professionOther})</span>
                          )}
                      </div>
                    </div>

                    <div className="card-section">
                      <div className="section-title">Interests</div>
                      <div id="interests">
                        {user.interests?.join(", ")}
                        {user.interestsOther &&
                          user.interests?.includes("Other") && (
                            <span> ({user.interestsOther})</span>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <br />
            <a
              href="/documents/ICEGEMS_Terms-and-Conditions.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="terms-link"
            >
              Terms & Conditions
            </a>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Profile;
